import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Inbox, Pin, BellOff, RefreshCw, Users, MoreVertical, RotateCcw, CheckCircle, CheckSquare, Square as SquareIcon, X, Pencil, Plus, MessageSquarePlus, UserPlus, Filter, FilterX } from 'lucide-react';
import api, { describeRequestError } from '../services/api';
import { activityLevel, activityLabel } from '../utils/activity';
import socketService from '../services/socketService';
import '../styles/ConversationList.css';

const TABS = [
  { key: 'pending', label: 'Aguardando', status: 'pending', color: '#F59E0B', dotColor: '#F59E0B' },
  { key: 'open',    label: 'Atendendo',  status: 'open',    color: '#10B981', dotColor: '#10B981' },
  { key: 'closed',  label: 'Resolvidos', status: 'closed',  color: '#94A3B8', dotColor: '#94A3B8' },
];

function avatarColor(name = '') {
  const colors = ['#4F46E5','#7C3AED','#DB2777','#DC2626','#EA580C','#D97706','#16A34A','#0891B2','#0284C7','#7C3AED'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '#';
}

let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return _audioCtx;
}

async function playNotificationSound() {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') await ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch (e) {}
}

function showBrowserNotification(name, text) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (!document.hidden) return;
  try {
    new Notification(`Nova mensagem de ${name}`, {
      body: text?.substring(0, 100) || 'Nova mensagem',
      icon: '/favicon.ico'
    });
  } catch (e) {}
}

// Desde quando o cliente está esperando: a ÚLTIMA mensagem dele. Fallback pra
// created_at só quando não existe mensagem de cliente (conversa iniciada pelo
// atendente). Usar created_at direto é errado — a linha da conversa é
// reaproveitada a cada reabertura, então ele guarda o primeiro contato de todos
// os tempos (mostrava "110d" pra quem escreveu hoje).
function waitingSince(conv) {
  return conv.last_client_message_at || conv.created_at || null;
}

function waitingMinutes(conv) {
  const since = waitingSince(conv);
  if (!since) return null;
  return Math.floor((Date.now() - new Date(since)) / 60000);
}

function waitingAge(conv) {
  const mins = waitingMinutes(conv);
  if (mins === null) return null;
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// Nível de atraso da espera, segundo os limites configurados no tenant:
//   ok → crítico. Alimenta a cor do relógio na lista.
function waitingLevel(conv, sla) {
  const mins = waitingMinutes(conv);
  if (mins === null || !sla) return 'ok';
  if (mins >= (sla.criticalHours || 6) * 60) return 'critical';
  if (mins >= (sla.warnHours || 2) * 60) return 'warn';
  return 'ok';
}

// Paleta estável de cores para departamentos. Mesmo nome → mesma cor sempre.
const DEPT_COLORS = [
  '#4F46E5', // indigo
  '#16A34A', // green
  '#DC2626', // red
  '#D97706', // amber
  '#0891B2', // cyan
  '#DB2777', // pink
  '#7C3AED', // violet
  '#0284C7', // sky
  '#65A30D', // lime
  '#EA580C', // orange
  '#0F766E', // teal
  '#9333EA'  // purple
];
function deptColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return DEPT_COLORS[Math.abs(hash) % DEPT_COLORS.length];
}

function ConversationList({ onSelectConversation, selectedId, isGroup = false, unreadByTab = {} }) {
  const [conversations, setConversations] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, open: 0, closed: 0 });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [tick, setTick] = useState(0);
  const [syncingGroups, setSyncingGroups] = useState(false);
  const [menuState, setMenuState] = useState(null); // { conv, x, y } ou null
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkResolving, setBulkResolving] = useState(false);
  // "Meus setores" — só admin/supervisor pode usar (atendentes já são filtrados)
  const currentUserRole = (JSON.parse(localStorage.getItem('user') || '{}').role) || '';
  const isStaff = currentUserRole === 'admin' || currentUserRole === 'supervisor';
  const [myDeptOnly, setMyDeptOnly] = useState(
    () => localStorage.getItem('conv_my_dept_only') === 'true'
  );
  const myDeptOnlyRef = useRef(myDeptOnly);
  useEffect(() => { myDeptOnlyRef.current = myDeptOnly; }, [myDeptOnly]);
  const toggleMyDeptOnly = () => {
    setMyDeptOnly(v => {
      const next = !v;
      localStorage.setItem('conv_my_dept_only', String(next));
      // Invalida o cache pra forçar refetch com o novo filtro
      tabCacheRef.current = {};
      return next;
    });
  };
  // Estado da pausa de emergência (anti-ban) — quando ativa, esconde botão
  // "Nova conversa" e barra do topo mostra aviso. Consulta uma vez no mount.
  const [safetyLock, setSafetyLock] = useState({ paused: false, until: null });
  useEffect(() => {
    api.get('/system/status').then(r => setSafetyLock({
      paused: !!r.data?.paused,
      until: r.data?.pausedUntil || null
    })).catch(() => {});
  }, []);

  // quota Nova conversa — 2026-06-01: contador diário por atendente, limite
  // configurável por tenant (default 8). Refletimos used/limit no botão e
  // bloqueamos a abertura do modal quando remaining===0. Erro silencioso
  // mantém defaults pra não travar a UI se /system/nova-conversa-quota cair.
  const [novaQuota, setNovaQuota] = useState({ used: 0, limit: 8, remaining: 8 });
  // Limites de espera do tenant — colorem o relógio da fila. Defaults iguais
  // aos do backend pra UI não ficar sem cor se o endpoint falhar.
  const [slaConfig, setSlaConfig] = useState({ enabled: 1, warnHours: 2, criticalHours: 6 });
  // Foto de perfil ampliada a partir da própria lista — { url, name }
  const [photoPreview, setPhotoPreview] = useState(null);
  useEffect(() => {
    if (!photoPreview) return;
    const onKey = (e) => { if (e.key === 'Escape') setPhotoPreview(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoPreview]);
  const refreshNovaQuota = useCallback(async () => {
    try {
      const r = await api.get('/system/nova-conversa-quota');
      const d = r.data || {};
      setNovaQuota({
        used: Number(d.used) || 0,
        limit: Number(d.limit) || 8,
        remaining: Number(d.remaining) || 0,
        resetsAt: d.resetsAt || null
      });
    } catch (e) {
      // silencioso — mantém defaults
    }
  }, []);
  useEffect(() => { refreshNovaQuota(); }, [refreshNovaQuota]);

  // Modal "Nova conversa"
  const [showNewConv, setShowNewConv] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [creatingConv, setCreatingConv] = useState(false);
  const [mutedIds, setMutedIds] = useState(() => new Set());
  const mutedIdsRef = useRef(new Set());
  useEffect(() => { mutedIdsRef.current = mutedIds; }, [mutedIds]);

  // Setores do usuário logado — usado pra decidir se uma mensagem nova
  // deve disparar som/notificação. Lê do cache localStorage; se não houver,
  // busca de /users/profile uma vez e armazena.
  const userInfoRef = useRef(null);
  useEffect(() => {
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const cachedDeptIds = JSON.parse(localStorage.getItem('user_dept_ids') || 'null');
    userInfoRef.current = {
      id: me.id,
      role: me.role,
      deptIds: Array.isArray(cachedDeptIds) ? cachedDeptIds : null
    };
    if (!userInfoRef.current.deptIds) {
      api.get('/users/profile').then(r => {
        const ids = (r.data?.departments || []).map(d => d.id);
        localStorage.setItem('user_dept_ids', JSON.stringify(ids));
        userInfoRef.current = { ...userInfoRef.current, deptIds: ids };
      }).catch(() => {});
    }
  }, []);

  // Decide se a mensagem é "atendível" pelo usuário atual (notifica só nesse caso).
  const shouldNotify = (data) => {
    const u = userInfoRef.current;
    if (!u) return true; // ainda carregando — comportamento permissivo
    // Admin/supervisor recebe tudo
    if (u.role === 'admin' || u.role === 'supervisor') return true;
    // Atribuída ao usuário atual → recebe
    if (data.assignedAgentId && String(data.assignedAgentId) === String(u.id)) return true;
    // Atribuída a OUTRO atendente → não recebe
    if (data.assignedAgentId && String(data.assignedAgentId) !== String(u.id)) return false;
    // Sem agente atribuído: depende do setor da conversa
    if (!data.departmentId) return true; // grupo público / fila sem setor
    const myDepts = u.deptIds || [];
    return myDepts.includes(Number(data.departmentId));
  };

  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // Limpa badge da conversa aberta
  useEffect(() => {
    if (selectedId) {
      setUnreadCounts(prev => ({ ...prev, [selectedId]: 0 }));
    }
  }, [selectedId]);

  // Solicita permissão de notificação uma única vez
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Ticker para atualizar tempo de espera a cada minuto
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const [loadError, setLoadError] = useState(null);

  // Refs pra que os handlers do socket sempre leiam o `tab`/`isGroup` atual
  // sem precisar re-inscrever. Antes, a useEffect grande dependia de `tab`
  // — cada mudança de aba desinscrevia/reinscrevia tudo, deixando a transição
  // travosa.
  const tabRef = useRef(tab);
  const isGroupRef = useRef(isGroup);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => { isGroupRef.current = isGroup; }, [isGroup]);

  // Cache por aba — voltar pra uma aba já visitada exibe instantâneo (sem wait).
  // Refresh em background atualiza com os dados frescos do servidor.
  const tabCacheRef = useRef({});

  // ── Paginação da lista (2026-09-25) ──
  // A aba Resolvidos trazia 504 conversas de uma vez (~388 KB por troca de aba).
  // Agora vem de 50 em 50 conforme rola.
  const LIST_PAGE = 50;
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Espelhos em ref: loadMoreConversations é um useCallback estável (pra não
  // recriar o handler de rolagem a cada render) e por isso não enxerga o state
  // atual — leria valores congelados do primeiro render.
  const conversationsRef = useRef([]);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const searchRef = useRef('');
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  useEffect(() => { hasMoreRef.current = hasMoreConversations; }, [hasMoreConversations]);

  // Quantas já estavam carregadas em cada aba. O refresh disparado por socket
  // recarrega a MESMA janela em vez de voltar pra 50 — senão, quem rolou até a
  // terceira página perdia tudo a cada mensagem que chegasse.
  const loadedCountRef = useRef({});

  const listParams = (activeTab) => {
    const p = isGroupRef.current ? { is_group: 1 } : { status: activeTab };
    if (myDeptOnlyRef.current && !isGroupRef.current) p.my_dept_only = 1;
    return p;
  };

  const fetchConversations = useCallback(async (activeTab = tabRef.current) => {
    const key = isGroupRef.current ? 'group' : activeTab;
    setLoadError(null);
    // Mostra cache imediatamente (se houver) — sem flicker
    if (tabCacheRef.current[key]) {
      setConversations(tabCacheRef.current[key]);
    } else {
      setLoading(true);
    }
    // A busca é filtrada no cliente (ver `filtered`), então ela só enxerga o que
    // está carregado. Com a lista paginada, procurar uma conversa antiga em
    // Resolvidos não acharia nada — regressão silenciosa e difícil de perceber.
    // Enquanto há texto na busca, carrega sem paginação (exatamente o
    // comportamento de antes); sem busca, pagina normalmente.
    const buscando = !!(searchRef.current || '').trim();
    // Recarrega a mesma quantidade que já estava na tela (ver loadedCountRef).
    const janela = Math.max(LIST_PAGE, loadedCountRef.current[key] || 0);
    const params = buscando
      ? listParams(activeTab)
      : { ...listParams(activeTab), limit: janela, offset: 0 };

    // A resposta pode chegar depois do usuário já ter trocado de aba (timeout de
    // 20s + 2s de retentativa é bastante tempo). Sem esta checagem, a lista da aba
    // antiga sobrescreve a que ele está olhando agora. O cache continua sendo
    // preenchido — o dado é válido, só não é mais o que está na tela.
    const aindaNaMesmaAba = () => (isGroupRef.current ? 'group' : tabRef.current) === key;

    // Uma retentativa automática em timeout/queda: o erro quase sempre é um pico
    // momentâneo do servidor, e obrigar o usuário a clicar "Tentar novamente" pra
    // algo que se resolve sozinho em 2s é ruído. Erro de permissão/500 não retenta
    // — insistir não muda o resultado.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await api.get('/conversations', { params });
        // Sem `limit` o backend devolve array puro (compatibilidade); com
        // `limit`, devolve { conversations, hasMore }.
        const paginado = !Array.isArray(response.data);
        const lista = paginado ? (response.data?.conversations || []) : response.data;
        tabCacheRef.current[key] = lista;
        loadedCountRef.current[key] = lista.length;
        if (aindaNaMesmaAba()) {
          setConversations(lista);
          setHasMoreConversations(paginado ? !!response.data?.hasMore : false);
          setLoadError(null);
          setLoading(false);
        }
        return;
      } catch (error) {
        // 401 é tratado pelo interceptor do axios (refresh/logout) — não vira banner.
        if (error.response?.status === 401) break;

        const { kind, message } = describeRequestError(error);
        console.error('Erro ao buscar conversas:', kind, error);

        // Se o usuário já saiu desta aba, o erro não é mais dele — abandona
        // silenciosamente em vez de pintar um banner sobre outra lista.
        if (!aindaNaMesmaAba()) return;

        const vaiRetentar = attempt === 0 && (kind === 'timeout' || kind === 'offline');
        if (vaiRetentar) {
          setLoadError(message);
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        // Última tentativa falhou — texto final, sem o "tentando de novo".
        setLoadError(
          kind === 'timeout'
            ? 'O servidor demorou demais para responder. Sua conexão está ok — tente novamente em instantes.'
            : message
        );
        break;
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Why-incidente 2026-09-22: isto disparava 3 GET /conversations (um por status)
  // só pra ler `.data.length`. Cada um roda 5 subqueries correlacionadas em
  // `messages` por linha, sem LIMIT — e como fetchCounts também é chamado nos
  // handlers de socket, toda mensagem recebida repetia as 3. Agora é uma única
  // chamada que só toca a tabela `conversations`.
  // Entrar ou sair da busca muda o modo de carregamento (tudo × paginado), então
  // exige refetch. Só dispara na TRANSIÇÃO — digitar mais letras dentro de uma
  // busca já ativa não recarrega nada, continua filtrando no cliente.
  useEffect(() => {
    const antes = !!(searchRef.current || '').trim();
    const agora = !!search.trim();
    searchRef.current = search;
    if (antes === agora) return;
    const t = setTimeout(() => fetchConversations(tabRef.current), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Próxima página, anexada ao fim. Diferente do chat, aqui o conteúdo entra
  // ABAIXO do que já está na tela — não desloca nada, então pode ser automático
  // na rolagem sem risco de a tela pular.
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const key = isGroupRef.current ? 'group' : tabRef.current;
    try {
      const atual = conversationsRef.current;
      const { data } = await api.get('/conversations', {
        params: { ...listParams(tabRef.current), limit: LIST_PAGE, offset: atual.length }
      });
      // Trocou de aba enquanto a página vinha: descarta. Sem isto, conversas de
      // Resolvidos seriam anexadas no fim da lista de Atendendo.
      const keyAgora = isGroupRef.current ? 'group' : tabRef.current;
      if (keyAgora !== key) return;
      const novas = data?.conversations || [];
      // Dedup por id: entre uma página e outra, uma conversa pode receber
      // mensagem e subir pro topo, deslocando o offset e repetindo linhas.
      if (novas.length) {
        const vistos = new Set(atual.map(c => c.id));
        const juntas = [...atual, ...novas.filter(c => !vistos.has(c.id))];
        tabCacheRef.current[key] = juntas;
        loadedCountRef.current[key] = juntas.length;
        setConversations(juntas);
      }
      setHasMoreConversations(!!data?.hasMore);
    } catch (e) {
      // Silencioso: falhar ao buscar MAIS não pode apagar o que já está na tela.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrega a próxima página ao chegar perto do fim da rolagem.
  const handleListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) loadMoreConversations();
  };

  const fetchCounts = useCallback(async () => {
    if (isGroupRef.current) return;
    try {
      const params = myDeptOnlyRef.current ? { my_dept_only: 1 } : {};
      const { data } = await api.get('/conversations/counts', { params });
      setCounts({ pending: data.pending, open: data.open, closed: data.closed });
    } catch (e) {}
  }, []);

  // Ao entrar na aba Grupos, sincroniza com a Evolution pra trazer todos os grupos
  // (não só os com mensagem recente). Só roda uma vez por mount.
  useEffect(() => {
    if (!isGroup) return;
    let cancelled = false;
    api.post('/whatsapp/sync-groups').finally(() => {
      if (!cancelled) fetchConversations();
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup]);

  useEffect(() => {
    fetchConversations();
    fetchCounts();

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.tenant_id) socketService.joinTenant(user.tenant_id);

    // Debounce: vários eventos socket (message:new + conversation:updated +
    // conversation:assigned ...) podem disparar em sequência. Sem debounce, cada
    // um chama fetchConversations e o `loading=true` faz o painel "piscar" cada vez.
    //
    // Why-incidente 2026-09-22: este bloco existia, mas ficava declarado DEPOIS de
    // onMessageNew — e o onMessageNew, que é o evento de maior frequência, chamava
    // fetchConversations/fetchCounts direto, sem passar por aqui. Numa rajada de
    // mensagens isso disparava uma query pesada por mensagem. Declarado antes pra
    // que todos os handlers usem o mesmo caminho.
    let refreshTimer = null;
    const refresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        fetchConversations();
        fetchCounts();
      }, 250);
    };

    const onMessageNew = (data) => {
      const isClient = data.senderType === 'client';
      const muted = mutedIdsRef.current.has(data.conversationId);
      // Só toca som / notifica se a conversa for atendível pelo usuário atual.
      if (isClient && !muted && shouldNotify(data)) {
        playNotificationSound();
        showBrowserNotification(data.senderName || 'Cliente', data.messageText);
      }
      if (data.conversationId !== selectedIdRef.current && isClient) {
        setUnreadCounts(prev => ({
          ...prev,
          [data.conversationId]: (prev[data.conversationId] || 0) + 1
        }));
      }
      refresh();
    };

    const onMuted = (data) => {
      const me = JSON.parse(localStorage.getItem('user') || '{}');
      if (data.userId !== me.id) return;
      setMutedIds(prev => {
        const next = new Set(prev);
        if (data.muted) next.add(data.conversationId); else next.delete(data.conversationId);
        return next;
      });
    };

    // Pin é compartilhado pelo tenant: fixou/desafixou, vale pra todos que
    // enxergam a conversa. Refetch em qualquer pin-changed, não importa quem fez.
    const onPinChanged = () => refresh();

    // Conversa passou do limite crítico de espera — avisa quem está com a tela
    // aberta. O push web (pra quem está fora) sai do worker no backend.
    const onSlaAlert = async (data) => {
      if (!data?.conversationId) return;
      try {
        const { showToast } = await import('../utils/toast');
        showToast(
          `⏰ ${data.contactName || 'Cliente'} aguardando há ${data.waitingHours}h sem atendimento`,
          'warning'
        );
      } catch {}
      refresh();
    };

    // Conversa lida (em qualquer sessão/dispositivo OU pelo celular):
    // limpa otimisticamente o badge daquela conv na lista E refetcha pra trazer
    // o novo unread_count do servidor.
    const onConvRead = (data) => {
      if (!data?.conversationId) return;
      setConversations(prev => prev.map(c =>
        c.id === data.conversationId ? { ...c, unread_count: 0 } : c
      ));
      setUnreadCounts(prev => ({ ...prev, [data.conversationId]: 0 }));
      refresh();
    };

    socketService.on('message:new', onMessageNew);
    socketService.on('conversation:updated', refresh);
    socketService.on('conversation:autoReplyBlocked', refresh);
    socketService.on('conversation:assigned', refresh);
    socketService.on('contact:updated', refresh);
    socketService.on('conversation:pin-changed', onPinChanged);
    socketService.on('conversation:sla', onSlaAlert);
    socketService.on('conversation:muted', onMuted);
    socketService.on('conversation:read', onConvRead);
    socketService.on('connect', refresh);

    // Lista inicial de conversas silenciadas pelo usuário atual
    api.get('/conversations/muted/ids')
      .then(r => setMutedIds(new Set(r.data?.ids || [])))
      .catch(() => {});

    // Limites de SLA (relógio amarelo/vermelho na fila)
    api.get('/system/sla-config')
      .then(r => r.data && setSlaConfig(r.data))
      .catch(() => {});

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('online', refresh);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      socketService.off('message:new', onMessageNew);
      socketService.off('conversation:updated', refresh);
      socketService.off('conversation:autoReplyBlocked', refresh);
      socketService.off('conversation:assigned', refresh);
      socketService.off('contact:updated', refresh);
      socketService.off('conversation:pin-changed', onPinChanged);
      socketService.off('conversation:sla', onSlaAlert);
      socketService.off('conversation:muted', onMuted);
      socketService.off('conversation:read', onConvRead);
      socketService.off('connect', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('online', refresh);
    };
    // Inscreve handlers só quando muda de modo (Atendimentos ↔ Grupos);
    // mudança de tab (Aguardando/Atendendo/Resolvidos) não recria nada.
  }, [isGroup, fetchConversations, fetchCounts]);

  // Tab change → só refetch (lê do cache, atualiza em bg). Sem reinscrever socket.
  useEffect(() => {
    if (isGroup) return;
    fetchConversations(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Toggle do filtro "Meus setores" → invalida cache e refetcha tudo
  useEffect(() => {
    if (isGroup) return;
    fetchConversations(tab);
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDeptOnly]);

  const handleSelect = (conv) => {
    // Em modo seleção múltipla, click marca/desmarca em vez de abrir
    if (selectMode) {
      toggleSelected(conv.id);
      return;
    }
    // Otimista: zera unread localmente e na lista. Servidor confirma via
    // socket conversation:read após persistir last_read_message_id.
    setUnreadCounts(prev => ({ ...prev, [conv.id]: 0 }));
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
    onSelectConversation(conv);
  };

  // ── Seleção múltipla / resolver em lote ──
  const enterSelectMode = (initialId = null) => {
    setSelectMode(true);
    setSelectedIds(new Set(initialId ? [initialId] : []));
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    setSelectedIds(new Set(filtered.map(c => c.id)));
  };
  const handleBulkResolve = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Resolver ${selectedIds.size} atendimento(s)?`)) return;
    setBulkResolving(true);
    try {
      const r = await api.post('/conversations/bulk-resolve', {
        conversationIds: [...selectedIds]
      });
      try { (await import('../utils/toast')).showToast(`${r.data?.resolved ?? 0} atendimento(s) resolvidos`, 'success'); } catch {}
      exitSelectMode();
      fetchConversations(tab);
      fetchCounts();
    } catch (err) {
      try { (await import('../utils/toast')).showToast(err.response?.data?.message || 'Falha ao resolver', 'error'); } catch {}
    } finally {
      setBulkResolving(false);
    }
  };
  // Saindo da aba ou trocando entre Atendimentos↔Grupos sai do modo seleção
  useEffect(() => { exitSelectMode(); /* eslint-disable-next-line */ }, [tab, isGroup]);

  // ── Context menu (botão direito / setinha de hover) ──
  const openMenu = (conv, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    // Posiciona perto do cursor; mantemos dentro da viewport
    const x = Math.min(ev.clientX, window.innerWidth - 220);
    const y = Math.min(ev.clientY, window.innerHeight - 120);
    setMenuState({ conv, x, y });
  };
  const closeMenu = () => setMenuState(null);

  // Fecha menu em clique fora / Esc / scroll
  useEffect(() => {
    if (!menuState) return;
    const onDoc = (e) => {
      if (e.target.closest('.conv-menu')) return;
      closeMenu();
    };
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [menuState]);

  // ── Nova conversa ──
  const openNewConv = async () => {
    setShowNewConv(true);
    setContactSearch('');
    setNewContactName('');
    setNewContactPhone('');
    try {
      const r = await api.get('/contacts');
      setAllContacts(Array.isArray(r.data) ? r.data : (r.data?.contacts || []));
    } catch {
      setAllContacts([]);
    }
  };
  const closeNewConv = () => setShowNewConv(false);

  const startConversationWith = async ({ contactNumber, contactName }) => {
    if (creatingConv) return;
    const digits = (contactNumber || '').replace(/\D/g, '');
    if (digits.length < 8) {
      try { (await import('../utils/toast')).showToast('Número inválido', 'error'); } catch {}
      return;
    }
    setCreatingConv(true);
    try {
      const r = await api.post('/conversations', {
        contactNumber: digits,
        contactName: (contactName || '').trim() || null
      });
      const convId = r.data?.id;
      const inProgress = !!r.data?.inProgress;
      closeNewConv();
      // Busca a conversa e abre na tela
      try {
        const cv = await api.get(`/conversations/${convId}`);
        if (cv.data) onSelectConversation(cv.data);
      } catch {}
      // Só troca pra aba Atendendo se a conversa foi atribuída a mim.
      // Se já estava com outro atendente (inProgress), respeita a aba atual.
      if (!inProgress) {
        if (tab !== 'open') setTab('open');
        fetchConversations('open');
      } else {
        fetchConversations(tab);
      }
      fetchCounts();
      // quota Nova conversa — 2026-06-01: atualiza contador depois de
      // criar com sucesso (servidor incrementou audit_log).
      refreshNovaQuota();
      try {
        const msg = inProgress
          ? 'Conversa já está com outro atendente — apenas abrindo'
          : (r.data?.reused ? 'Atendimento aberto' : 'Conversa iniciada');
        (await import('../utils/toast')).showToast(msg, inProgress ? 'info' : 'success');
      } catch {}
    } catch (err) {
      // quota Nova conversa — 2026-06-01: 429 com code específico significa
      // que o atendente bateu o limite diário. Sincroniza o estado pra refletir
      // exatamente o que o servidor diz e mostra mensagem dele.
      const status = err.response?.status;
      const code = err.response?.data?.code;
      if (status === 429 && code === 'NOVA_CONVERSA_QUOTA_EXCEEDED') {
        refreshNovaQuota();
        try {
          (await import('../utils/toast')).showToast(
            err.response?.data?.message || 'Limite diário de novas conversas atingido',
            'error'
          );
        } catch {
          alert(err.response?.data?.message || 'Limite diário de novas conversas atingido');
        }
      } else {
        try { (await import('../utils/toast')).showToast(err.response?.data?.message || 'Falha ao iniciar conversa', 'error'); } catch {}
      }
    } finally {
      setCreatingConv(false);
    }
  };

  const handleTogglePin = async (conv) => {
    closeMenu();
    const next = !conv.is_pinned;
    // Otimista
    setConversations(prev => prev.map(c =>
      c.id === conv.id ? { ...c, is_pinned: next ? 1 : 0 } : c
    ));
    try {
      if (next) {
        await api.put(`/conversations/${conv.id}/pin`);
        try { (await import('../utils/toast')).showToast('Conversa fixada para toda a equipe — não será encerrada automaticamente', 'success'); } catch {}
      } else {
        await api.delete(`/conversations/${conv.id}/pin`);
        try { (await import('../utils/toast')).showToast('Conversa desafixada para toda a equipe', 'info'); } catch {}
      }
    } catch (err) {
      // Reverte
      setConversations(prev => prev.map(c =>
        c.id === conv.id ? { ...c, is_pinned: next ? 0 : 1 } : c
      ));
      try { (await import('../utils/toast')).showToast(err.response?.data?.message || 'Falha ao alterar fixação', 'error'); } catch {}
    }
  };

  const handleRename = async (conv) => {
    const current = conv.contact_name || '';
    const next = window.prompt(
      conv.is_group ? 'Novo nome do grupo:' : 'Novo nome do contato:',
      current
    );
    closeMenu();
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;
    try {
      await api.put(`/conversations/${conv.id}/contact-name`, { name: trimmed });
      try { (await import('../utils/toast')).showToast('Nome atualizado', 'success'); } catch {}
      // Otimista — atualiza na lista
      setConversations(prev => prev.map(c =>
        c.id === conv.id ? { ...c, contact_name: trimmed } : c
      ));
      fetchConversations(tab);
    } catch (err) {
      try { (await import('../utils/toast')).showToast(err.response?.data?.message || 'Falha ao renomear', 'error'); } catch {}
    }
  };

  const handleReopen = async (conv) => {
    closeMenu();
    try {
      await api.put(`/conversations/${conv.id}/reopen`);
      try { (await import('../utils/toast')).showToast('Atendimento reaberto e atribuído a você', 'success'); }
      catch {}
      fetchConversations(tab);
      fetchCounts();
    } catch (err) {
      try { (await import('../utils/toast')).showToast(err.response?.data?.message || 'Falha ao reabrir', 'error'); }
      catch {}
    }
  };

  const handleSyncGroups = async () => {
    if (syncingGroups) return;
    setSyncingGroups(true);
    try {
      const res = await api.post('/whatsapp/sync-groups?force=1');
      const d = res.data || {};
      // Mostra resultado curto: total de grupos da Evolution + criados/atualizados
      const msg = `${d.total ?? 0} grupos sincronizados${d.created ? ` · ${d.created} novos` : ''}${d.updated ? ` · ${d.updated} atualizados` : ''}`;
      // Toast simples — usa alerta nativo se não houver showToast importado
      try { (await import('../utils/toast')).showToast(msg, 'success'); }
      catch { console.log(msg); }
      fetchConversations(tab);
    } catch (e) {
      try { (await import('../utils/toast')).showToast('Falha ao sincronizar', 'error'); }
      catch { alert('Falha ao sincronizar grupos'); }
    } finally {
      setSyncingGroups(false);
    }
  };

  const filtered = conversations.filter(conv =>
    (conv.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (conv.contact_number || '').includes(search)
  );

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="conversation-list">
      <div className="list-header">
        <div className="list-header-top">
          <h3>{isGroup ? 'Grupos' : 'Atendimentos'}</h3>
          {isGroup && (
            <button
              className={`refresh-groups-btn ${syncingGroups ? 'is-syncing' : ''}`}
              onClick={handleSyncGroups}
              disabled={syncingGroups}
              title="Atualizar lista de grupos"
            >
              <RefreshCw size={14}/> {syncingGroups ? 'Sincronizando...' : 'Atualizar'}
            </button>
          )}
          {/* Filtro "Meus setores" — admin/supervisor pode reduzir o escopo */}
          {!isGroup && !selectMode && isStaff && (
            <button
              className={`refresh-groups-btn ${myDeptOnly ? 'is-syncing' : ''}`}
              onClick={toggleMyDeptOnly}
              title={myDeptOnly
                ? 'Mostrando apenas conversas do(s) seu(s) setor(es). Clique pra ver todas.'
                : 'Mostrar apenas conversas do(s) seu(s) setor(es)'}
            >
              {myDeptOnly ? <FilterX size={14}/> : <Filter size={14}/>}
              {myDeptOnly ? 'Meu setor' : 'Todos'}
            </button>
          )}
          {/* Iniciar nova conversa — só em Atendimentos. Bloqueado durante
             pausa de emergência (proteção anti-ban).
             quota Nova conversa — 2026-06-01: tambem bloqueia quando atendente
             atinge limite diário (default 8) — mostra used/limit no texto. */}
          {!isGroup && !selectMode && !safetyLock.paused && (() => {
            const quotaExhausted = novaQuota.remaining === 0;
            const resetsAtLabel = novaQuota.resetsAt
              ? new Date(novaQuota.resetsAt).toLocaleString('pt-BR')
              : 'meia-noite';
            const baseTitle = `Iniciar nova conversa (${novaQuota.used}/${novaQuota.limit} hoje · reseta em ${resetsAtLabel})`;
            const exhaustedTitle = `Limite diário de ${novaQuota.limit} novas conversas atingido. Resetará em ${resetsAtLabel}.`;
            return (
              <button
                className={`refresh-groups-btn ${quotaExhausted ? 'is-quota-exhausted' : ''}`}
                onClick={openNewConv}
                disabled={quotaExhausted}
                title={quotaExhausted ? exhaustedTitle : baseTitle}
                style={quotaExhausted ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <MessageSquarePlus size={14}/> Nova ({novaQuota.used}/{novaQuota.limit})
              </button>
            );
          })()}
          {!isGroup && !selectMode && safetyLock.paused && (
            <button
              className="refresh-groups-btn"
              disabled
              title={`Iniciar novas conversas bloqueado até ${new Date(safetyLock.until).toLocaleString('pt-BR')} (proteção anti-ban). Recebimento e respostas continuam normais.`}
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              <MessageSquarePlus size={14}/> Bloqueado
            </button>
          )}
          {/* Modo seleção múltipla — só faz sentido em conversas ativas (Aguardando/Atendendo) */}
          {!isGroup && tab !== 'closed' && !selectMode && (
            <button
              className="refresh-groups-btn"
              onClick={() => enterSelectMode()}
              title="Selecionar vários atendimentos"
            >
              <CheckSquare size={14}/> Selecionar
            </button>
          )}
          {selectMode && (
            <button
              className="refresh-groups-btn"
              onClick={exitSelectMode}
              title="Sair da seleção"
            >
              <X size={14}/> Cancelar
            </button>
          )}
        </div>
        <div className="search-box">
          <Search size={15} />
          <input
            type="text"
            placeholder="Buscar contato ou número..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {!isGroup && <div className="filter-tabs">
          {TABS.map(t => {
            const unread = unreadByTab[t.key] || 0;
            return (
              <button
                key={t.key}
                className={`filter-tab tab-${t.key} ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                <span className="tab-count">{counts[t.key] ?? 0}</span>
                {unread > 0 && <span className="tab-unread-dot" title={`${unread} nova(s)`}>{unread}</span>}
              </button>
            );
          })}
        </div>}
      </div>

      <div className="list-content" onScroll={handleListScroll}>
        {/* "Carregando" só aparece quando NÃO temos dados ainda — refreshes em
           background não escondem a lista (evita o efeito de "piscar"). */}
        {loading && conversations.length === 0 && (
          <div className="list-loading">Carregando...</div>
        )}
        {loadError && (
          <div className="list-error">
            ⚠️ {loadError}
            <button onClick={() => fetchConversations(tab)}>Tentar novamente</button>
          </div>
        )}

        {filtered.length === 0 && !loading && !isGroup && (
          <div className="list-empty">
            <Inbox size={36} />
            <p>Nenhuma conversa aqui.</p>
            <small>Novos clientes aparecerão automaticamente.</small>
          </div>
        )}
        {filtered.length === 0 && !loading && isGroup && (
          <div className="list-empty">
            <Users size={36} />
            <p>Nenhum grupo encontrado.</p>
            <small>
              Para um grupo aparecer aqui, adicione o número WhatsApp conectado ao SaaS
              como participante do grupo. Depois clique em <strong>Atualizar</strong> acima.
            </small>
            <button
              className="refresh-groups-btn"
              onClick={handleSyncGroups}
              disabled={syncingGroups}
              style={{ marginTop: 12 }}
            >
              <RefreshCw size={14}/> {syncingGroups ? 'Sincronizando...' : 'Atualizar agora'}
            </button>
          </div>
        )}

        {filtered.map(conv => {
          const tabMeta = TABS.find(t => t.status === conv.status) || TABS[0];
          const name = conv.contact_name || 'Sem Nome';
          // Conta vem do servidor (id > last_read_message_id). Local override
          // mantém compat com lógica antiga de socket caso a lista esteja stale.
          const unread = conv.unread_count != null
            ? Number(conv.unread_count) || 0
            : (unreadCounts[conv.id] || 0);
          return (
            <div
              key={conv.id}
              className={`conversation-item ${selectedId === conv.id ? 'active' : ''} ${conv.is_pinned ? 'is-pinned' : ''} ${selectMode ? 'select-mode' : ''} ${selectedIds.has(conv.id) ? 'is-selected' : ''}`}
              onClick={() => handleSelect(conv)}
              onContextMenu={(e) => openMenu(conv, e)}
            >
              {selectMode && (
                <span className="conv-checkbox" aria-hidden>
                  {selectedIds.has(conv.id)
                    ? <CheckSquare size={18} />
                    : <SquareIcon size={18} />}
                </span>
              )}
              {/* Wrapper só pra ancorar o ponto de atividade no canto do avatar.
                  Ele vira o filho flex no lugar do avatar, então o layout da
                  linha não muda. A ordem img → div é preservada porque o
                  onError abaixo depende de nextSibling. */}
              <span className="conv-avatar-wrap">
                {conv.profile_photo_url ? (
                  /* Clicar na foto AQUI na lista abre a imagem ampliada, sem
                     entrar na conversa. stopPropagation impede que o clique
                     borbulhe pro item e selecione o atendimento. */
                  <img
                    className="conv-avatar conv-avatar-photo conv-avatar-clickable"
                    src={conv.profile_photo_url}
                    alt={name}
                    title="Ver foto de perfil"
                    onClick={(e) => { e.stopPropagation(); setPhotoPreview({ url: conv.profile_photo_url, name }); }}
                    onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                  />
                ) : null}
                <div className="conv-avatar" style={{ background: avatarColor(name), display: conv.profile_photo_url ? 'none' : 'flex' }}>
                  {getInitials(name)}
                </div>
                {/* Atividade do contato — NÃO é presença do WhatsApp (ver
                    utils/activity.js). Só em conversas individuais: em grupo,
                    "fulano escreveu" não diz nada sobre quem está disponível.
                    Some depois de 24h pra não virar enfeite em toda linha. */}
                {!conv.is_group && activityLevel(conv.last_client_message_at) && (
                  <span
                    className={`conv-activity-dot conv-activity-${activityLevel(conv.last_client_message_at)}`}
                    title={activityLabel(conv.last_client_message_at)}
                  />
                )}
              </span>
              {/* Setinha que aparece no hover — abre o context menu */}
              <button
                className="conv-menu-trigger"
                onClick={(e) => openMenu(conv, e)}
                title="Mais opções"
              >
                <MoreVertical size={16}/>
              </button>
              <div className="conv-content">
                <div className="conv-top-row">
                  <span className="conv-name">
                    {conv.is_pinned ? <Pin size={12} className="conv-pin-icon" /> : null}
                    {(conv.is_muted || mutedIds.has(conv.id)) ? <BellOff size={12} className="conv-mute-icon" /> : null}
                    {conv.is_bot ? <span title="Marcado como bot — sem respostas automáticas" style={{ marginRight: 2 }}>🤖</span> : null}
                    {(!conv.is_bot && conv.auto_reply_blocked) ? <span title="Possível loop com bot — automação pausada" style={{ marginRight: 2 }}>⚠️</span> : null}
                    {name}
                  </span>
                  <span className="conv-time">{formatTime(conv.last_message_at || conv.updated_at)}</span>
                </div>
                <div className="conv-mid-row">
                  {!conv.is_group && (
                    <>
                      <span className="conv-status-dot" style={{ background: tabMeta.dotColor }} title={tabMeta.label} />
                      <span className="conv-number">{conv.contact_number}</span>
                    </>
                  )}
                  {!!conv.is_group && <span className="conv-group-tag">👥 Grupo</span>}
                  {/* Reloginho de espera só faz sentido em individuais. Em grupos,
                     não há "fila de atendimento" — ficaria eterno mesmo após ler tudo.
                     Conta desde a última mensagem DO CLIENTE (ver waitingSince). */}
                  {!conv.is_group && tab === 'pending' && waitingSince(conv) && (() => {
                    const nivel = waitingLevel(conv, slaConfig);
                    const rotulo = nivel === 'critical'
                      ? 'ATRASADO — cliente esperando muito tempo'
                      : nivel === 'warn' ? 'Atenção: espera acima do normal' : 'Tempo de espera';
                    return (
                      <span
                        className={`wait-badge wait-badge-${nivel}`}
                        title={`${rotulo}\nSem resposta desde ${new Date(waitingSince(conv)).toLocaleString()}`}
                      >
                        ⏱ {waitingAge(conv)}
                      </span>
                    );
                  })()}
                  {unread > 0 && <span className="unread-badge">{unread > 99 ? '99+' : unread}</span>}
                </div>
                {conv.last_message && (
                  <p className="conv-last-msg">{conv.last_message.substring(0, 65)}</p>
                )}
                <div className="conv-bottom-row">
                  {conv.department_name && (
                    <span
                      className="conv-dept-badge"
                      style={{ background: conv.department_color || deptColor(conv.department_name) }}
                    >
                      {conv.department_name}
                    </span>
                  )}
                  {/* Conversa sem setor fica invisível pra quem filtra por setor —
                      a auditoria achou 5 abandonadas exatamente assim. */}
                  {!conv.is_group && !conv.department_name && (
                    <span className="conv-dept-badge conv-dept-none" title="Sem setor definido">
                      sem setor
                    </span>
                  )}
                  {conv.agent_name && <span className="conv-agent-text">↳ {conv.agent_name}</span>}
                </div>
              </div>
            </div>
          );
        })}
        {/* Rodapé da paginação. Só aparece quando há mais páginas e não há busca
            ativa (buscando, a lista vem inteira — ver fetchConversations). */}
        {hasMoreConversations && !search.trim() && (
          <div className="list-more">
            {loadingMore
              ? 'Carregando mais conversas…'
              : <button className="list-more-btn" onClick={loadMoreConversations}>Carregar mais</button>}
          </div>
        )}
      </div>

      {selectMode && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">
            {selectedIds.size} selecionado{selectedIds.size === 1 ? '' : 's'}
          </span>
          <button
            className="bulk-btn-ghost"
            onClick={selectAll}
            disabled={!filtered.length || selectedIds.size === filtered.length}
          >
            Selecionar tudo ({filtered.length})
          </button>
          <button
            className="bulk-btn-resolve"
            onClick={handleBulkResolve}
            disabled={!selectedIds.size || bulkResolving}
          >
            <CheckCircle size={14}/> {bulkResolving ? 'Resolvendo...' : 'Resolver'}
          </button>
          <button className="bulk-btn-ghost" onClick={exitSelectMode}>
            Cancelar
          </button>
        </div>
      )}

      {showNewConv && (
        <div className="new-conv-overlay" onClick={closeNewConv}>
          <div className="new-conv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="new-conv-header">
              <h3><MessageSquarePlus size={16}/> Iniciar nova conversa</h3>
              <button className="new-conv-close" onClick={closeNewConv}><X size={16}/></button>
            </div>

            <div className="new-conv-search">
              <Search size={14}/>
              <input
                type="text"
                placeholder="Buscar contato por nome ou número..."
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className="new-conv-list">
              {(() => {
                const q = contactSearch.trim().toLowerCase();
                const list = allContacts.filter(c => {
                  if (!q) return true;
                  return (c.name || '').toLowerCase().includes(q)
                    || (c.phone_number || '').includes(q);
                }).slice(0, 100);
                if (!allContacts.length) {
                  return <p className="new-conv-empty">Nenhum contato salvo. Adicione um abaixo.</p>;
                }
                if (!list.length) {
                  return <p className="new-conv-empty">Nenhum resultado para "{contactSearch}".</p>;
                }
                return list.map(c => (
                  <button
                    key={c.id}
                    className="new-conv-contact"
                    onClick={() => startConversationWith({
                      contactNumber: c.phone_number,
                      contactName: c.name
                    })}
                    disabled={creatingConv}
                  >
                    <div className="new-conv-avatar" style={{ background: avatarColor(c.name || c.phone_number) }}>
                      {c.profile_photo_url
                        ? <img src={c.profile_photo_url} alt="" />
                        : getInitials(c.name || c.phone_number)}
                    </div>
                    <div className="new-conv-info">
                      <span className="new-conv-name">{c.name || 'Sem nome'}</span>
                      <span className="new-conv-phone">{c.phone_number}</span>
                    </div>
                  </button>
                ));
              })()}
            </div>

            <div className="new-conv-divider"><span>ou adicione um novo</span></div>

            <div className="new-conv-form">
              <label>Nome (opcional)</label>
              <input
                type="text"
                placeholder="Ex.: João da Silva"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
              />
              <label>Número (com DDI/DDD)</label>
              <input
                type="tel"
                placeholder="Ex.: 5511999999999"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
              />
              <button
                className="new-conv-submit"
                onClick={() => startConversationWith({
                  contactNumber: newContactPhone,
                  contactName: newContactName
                })}
                disabled={creatingConv || !newContactPhone.trim()}
              >
                <UserPlus size={14}/> {creatingConv ? 'Iniciando...' : 'Adicionar e iniciar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {menuState && (
        <div
          className="conv-menu"
          style={{ left: menuState.x, top: menuState.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="conv-menu-header">
            {menuState.conv.contact_name || 'Conversa'}
          </div>
          <button className="conv-menu-item" onClick={() => handleRename(menuState.conv)}>
            <Pencil size={14}/> {menuState.conv.is_group ? 'Renomear grupo' : 'Renomear contato'}
          </button>
          {/* Fixar/Desafixar — só em individuais em Atendendo. O pin é da EQUIPE:
              vale pra todos os atendentes/admins e blinda a conversa do
              encerramento automático. Limite de 10 por tenant (backend). */}
          {!menuState.conv.is_group && menuState.conv.status === 'open' && (
            <button className="conv-menu-item" onClick={() => handleTogglePin(menuState.conv)}>
              {menuState.conv.is_pinned
                ? <><Pin size={14} style={{ transform: 'rotate(45deg)' }}/> Desafixar (todos)</>
                : <><Pin size={14}/> Fixar para todos</>}
            </button>
          )}
          {/* Resolvida fica sem atendente — reabrir é "pegar pra si": vai
              direto pro Atendendo de quem clicou. Pra devolver pra outra
              pessoa, use Transferir no painel da conversa. */}
          {menuState.conv.status === 'closed' && (
            <button className="conv-menu-item" onClick={() => handleReopen(menuState.conv)}>
              <RotateCcw size={14}/> Pegar para mim (reabre)
            </button>
          )}
        </div>
      )}

      {/* Foto de perfil ampliada, aberta direto da lista */}
      {photoPreview && (
        <div className="photo-preview-overlay" onClick={() => setPhotoPreview(null)}>
          <div className="photo-preview-box" onClick={e => e.stopPropagation()}>
            <img src={photoPreview.url} alt={photoPreview.name} className="photo-preview-img" />
            <div className="photo-preview-footer">
              <span className="photo-preview-name">{photoPreview.name}</span>
              <button className="photo-preview-close" onClick={() => setPhotoPreview(null)}>✕ Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversationList;
